import { Injectable } from '@angular/core';
import { ConnectionService } from '@services/connection-service';
import { AppConfig } from '../../app/app.config';
import { RecentlyViewedData, RecentlyViewedConfig, RecentlyViewedLocation, RecentlyViewedEnum } from '@shared/types/recently-viewed.types';

@Injectable({
  providedIn: 'root'
})
export class RecentlyViewedProductsService {

  constructor(
    private readonly connectionService: ConnectionService,
    private readonly appConfig: AppConfig
  ) {}

  async getRecentlyViewedData(
    location: RecentlyViewedLocation,
    isFullPage: boolean = false,
  ): Promise<RecentlyViewedData | null> {

    const currentUser = this.connectionService.getActingUser();
    const isUnpaidUser = currentUser?.get('orderState') !== this.appConfig.Shared.User.OrderState.DELIVERED;
    if (isUnpaidUser) {
      return null;
    }
    const config = this.getDefaultConfig();

    try {
      const limit = isFullPage ? undefined : this.getProductLimit(location, config);

      const products = await this.connectionService.fetchRecentlyViewedProducts(config.numberOfDays, limit);
      const recentlyViewedProducts = products || [];

      const shouldShowComponent = this.shouldRenderComponent(
        isFullPage,
        recentlyViewedProducts.length,
        config.minProductCount
      );

      return {
        items: recentlyViewedProducts,
        shouldShowComponent,
        config
      };

    } catch (error) {
      return {
        items: [],
        shouldShowComponent: false,
        config: null,
      };
    }
  }

  getDefaultConfig(): RecentlyViewedConfig {
    return {
      numberOfDays: RecentlyViewedEnum.Defaults.NUMBER_OF_DAYS,
      minProductCount: RecentlyViewedEnum.Defaults.MIN_PRODUCT_COUNT,
      countOfProductsHP: RecentlyViewedEnum.Defaults.COUNT_OF_PRODUCTS_HP,
      countOfProductsSP: RecentlyViewedEnum.Defaults.COUNT_OF_PRODUCTS_SP,
      tags: {}
    };
  }

  private getProductLimit(location: string, config: RecentlyViewedConfig): number {
    switch (location) {
      case RecentlyViewedEnum.Location.HOME:
        return config.countOfProductsHP;
      case RecentlyViewedEnum.Location.SHOP:
        return config.countOfProductsSP;
      default:
        return config.countOfProductsHP;
    }
  }

  private shouldRenderComponent(
    isFullPage: boolean,
    productCount: number,
    minProductCount: number
  ): boolean {
    if (isFullPage) {
      return true;
    }
    return productCount >= minProductCount;
  }

  getProductTag(recentView: any, config: RecentlyViewedConfig | null): { text: string; show: boolean, type: string } {
    const previouslyBoughtTag = this.getPreviouslyBoughtTag(recentView);
    if (previouslyBoughtTag) return previouslyBoughtTag;

    const bestSellerTag = this.getBestSellerTag(recentView);
    if (bestSellerTag) return bestSellerTag;

    const customTag = this.getCustomTag(recentView, config);
    if (customTag) return customTag;

    return { text: '', show: false, type: RecentlyViewedEnum.TagType.CUSTOM };
  }

  private getPreviouslyBoughtTag(recentView: any): { text: string; show: boolean, type: string } | null {
    if (recentView?.isPreviouslyBought) {
      return { text: 'Previously Bought', show: true, type: RecentlyViewedEnum.TagType.PREVIOUSLY_BOUGHT };
    }
    return null;
  }

  private getBestSellerTag(recentView: any): { text: string; show: boolean, type: string } | null {
    if (recentView?.product?.isBestSeller) {
      return { text: 'Best Seller', show: true, type: RecentlyViewedEnum.TagType.BEST_SELLER };
    }
    return null;
  }

  private getCustomTag(recentView: any, config: RecentlyViewedConfig | null): { text: string; show: boolean, type: string } | null {
    const customTagText = this.getCustomTagText(recentView, config);
    if (!customTagText) return null;

    const tagType = this.determineTagType(customTagText);
    return { text: customTagText, show: true, type: tagType };
  }

  private getCustomTagText(recentView: any, config: RecentlyViewedConfig | null): string | null {
    if (!config?.tags || !recentView?.product?.objectId) return null;
    return config.tags[recentView.product.objectId] || null;
  }

  private determineTagType(tagText: string): string {
    const lowerCaseTag = tagText.toLowerCase();

    if (lowerCaseTag === 'previously bought') {
      return RecentlyViewedEnum.TagType.PREVIOUSLY_BOUGHT;
    }

    if (lowerCaseTag === 'best seller') {
      return RecentlyViewedEnum.TagType.BEST_SELLER;
    }

    return RecentlyViewedEnum.TagType.CUSTOM;
  }
}
